<!-- The three bets with the most evidence behind them, at the top of Today.

     WHY IT IS ABOVE THE FEED. The feed is a clock and answers "what starts
     next"; this answers "what should I bet", which is the question somebody
     opens the app with. It sits directly under the Now rule and above the
     first kick-off cluster — the one place on the screen that is neither a
     time of day nor a strategy, and the place the screen actually opens at.
     The top of the roll would be the other reading of "the top of Today", and
     it is two thousand pixels of yesterday's record above where anybody is
     looking. See the note where TodayPage puts it.

     AND IT IS A SHEET, NOT A BANNER. Everything else on this screen is paper;
     a coloured strip across the top would read as an advertisement for the
     app's own opinion. The three rows are numbered because the ranking is the
     whole content — three bets in no order is a list, three in an order is an
     answer.

     WHAT IS NOT HERE: a Place button. This sheet is the argument, the ticket
     below it is the paper, and putting the action in two places would be two
     records of one bet. Each row names its ticket and the reader scrolls to it.

     The arithmetic is `lib/best-bets.js` and none of it is here. -->
<template>
  <!-- THE SECTION IS ALWAYS HERE. Only the SHEET comes and goes.

       The label, the record strip and the ⓘ sit on the desk, where every other
       section's eyebrow sits, and they are on the screen on the mornings
       nothing clears as well as on the mornings something does. That is the
       fix: a reader cannot look for a thing that only exists when it has
       something to say, and the owner did not see the one line of small print
       this used to collapse to.

       The reviewer's rule is untouched — NO EMPTY CARDS. When nothing cleared
       there is no paper: the quiet line goes on the desk under the strip, in
       the same small print the feed's own empty line takes. -->
  <section class="best-section" :class="{ 'best-section--empty': model.empty }">
    <div class="best-head">
      <h2 class="section-title best-title">{{ model.section }}</h2>
      <!-- Icon-only and 44px, the same ⓘ the feed hangs in a ticket's corner.
           What it opens is the bar itself, in the words `engine/proof.js`
           writes once for the whole app. -->
      <IonButton
        fill="clear"
        size="small"
        class="best-info"
        aria-label="What is the evidence bar?"
        @click="emit('explain')"
      >
        <IonIcon slot="icon-only" :icon="informationCircleOutline" aria-hidden="true" />
      </IonButton>
    </div>

    <!-- The record first, because it is the only number in this section that is
         about what has already happened, and because on a quiet morning it is
         the one thing here that is still moving. Carbon unless the interval
         clears zero, which is the app's one rule for colouring a claim. -->
    <p v-if="record" class="small-print best-record" :style="{ color: record.ink }">{{ record.text }}</p>

    <p v-if="model.empty" class="small-print best-none">{{ model.emptyText }}</p>

    <div v-else class="sheet best-bets">
      <ol class="best-list">
        <li v-for="bet in model.bets" :key="bet.id" class="best-row">
          <span class="best-rank" aria-hidden="true">{{ bet.rank }}</span>
          <div class="best-body">
            <p class="best-name">
              <strong>{{ bet.name }}</strong>
              <span class="best-chance">{{ bet.chance }}</span>
            </p>
            <p class="best-reason">{{ bet.reason }}</p>
            <!-- The how and the when on two lines, not one. The how is printed
                 numbers and takes the print font, which is wide: run together
                 at 320 they wrap into "· Bet / now" and the instruction, the
                 half a reader acts on, arrives orphaned on its own line. -->
            <p class="best-how">{{ bet.how }}</p>
            <p class="best-when">{{ bet.when }}</p>
            <p v-if="bet.clv" class="small-print best-clv" :style="{ color: bet.clv.ink }">{{ bet.clv.text }}</p>
          </div>
        </li>
      </ol>

      <p class="small-print best-honesty">{{ model.honesty }}</p>
    </div>
  </section>
</template>

<script setup>
import { IonButton, IonIcon } from '@ionic/vue';
import { informationCircleOutline } from 'ionicons/icons';

defineProps({
  /** Exactly what `bestBets()` returns. This component does no arithmetic. */
  model: { type: Object, required: true },
  /** The strip's line, from `recordStrip()`. Never null on the screen. */
  record: { type: Object, default: null },
});
const emit = defineEmits(['explain']);
</script>

<style scoped>
/* One section, between the Now rule and the first kick-off cluster. The margin
   below is the ordinary gap between two sheets; the rule above it brings its
   own. NO `data-feed-key`: that namespace belongs to the feed's own sections
   and the three jumps walk it, and a marker there for a section that is not a
   feed section would be a jump target nobody meant. */
.best-section {
  margin: 0 0 var(--sp-4);
}

/* The eyebrow and its ⓘ on one row, the label baseline-left and the icon at
   the end of the line. The row is as tall as the 44px button, which is why the
   title's own top margin comes off: `.section-title` carries `--sp-5` above it
   for a heading that stands alone, and here the rule above is the separation. */
.best-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
  min-height: 44px;
}

.best-title {
  margin: 0;
}

/* Icon-only, so the label is the only thing the button owes anybody. Pulled
   out to the edge of the scroller the way the ticket's ⓘ is: the padding is
   the tap target, not the ink. */
.best-info {
  --padding-start: var(--sp-2);
  --padding-end: var(--sp-2);
  margin: 0 calc(var(--sp-2) * -1) 0 0;
  min-height: 44px;
  color: var(--carbon-faint);
}

.best-record {
  margin: 0 var(--sp-1) var(--sp-2);
}

.best-bets {
  margin: 0;
  padding: var(--sp-3);
}

.best-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

/* THE NUMBER IS A COLUMN, NOT A BULLET. A ranking read down the left edge is
   legible at a glance on a 320px screen; an inline "1." disappears into the
   strategy name beside it. `minmax(0, 1fr)` is what stops a long reason
   sentence pushing the sheet wider than the page. */
.best-row {
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr);
  gap: var(--sp-2);
  padding: var(--sp-2) 0;
  border-top: 1px solid var(--rule);
}

.best-row:first-child {
  border-top: 0;
  padding-top: 0;
}

.best-rank {
  font-family: var(--font-print);
  font-size: 1.1rem;
  line-height: 1.3;
  color: var(--carbon-faint);
}

.best-body > p {
  margin: 0 0 2px;
  overflow-wrap: anywhere;
}

.best-body > :last-child {
  margin-bottom: 0;
}

.best-name {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-1);
  align-items: baseline;
  justify-content: space-between;
}

/* The chance rides on the name line because that is the line anybody reads,
   and the honesty note at the foot promises it is printed on each. */
.best-chance {
  color: var(--carbon-faint);
  font-size: 0.85rem;
}

.best-how {
  font-family: var(--font-print);
}

/* The instruction, in the app's reading face rather than its printing one: it
   is a sentence, not a number. */
.best-when {
  color: var(--carbon-faint);
}

.best-honesty {
  margin: var(--sp-3) 0 0;
}

/* THE EMPTY STATE IS ON THE DESK, NOT ON PAPER. A sheet is where the app has
   something to say; a line under an eyebrow is where it says it has not. The
   side margin matches `.feed-note`'s so the line sits in the roll rather than
   floating out of it. */
.best-none {
  margin: 0 var(--sp-1);
}

/* At 320 the rank column gives up a quarter of its width before the sentence
   beside it starts wrapping mid-word. */
@media (max-width: 340px) {
  .best-row {
    grid-template-columns: 1.1rem minmax(0, 1fr);
    gap: var(--sp-1);
  }
}
</style>
